

import type React from "react"
import { useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTheme } from "../contexts/ThemeContext"
import { setShowProductModal } from "../features/ui/uiSlice"
import type { RootState } from "../store"
import ProductModal from "./ProductModal"
import { useTranslation } from "../hooks/useTranslation"
import CurrencyDisplay from "./ui/CurrencyDisplay"

interface ProductCardProps {
  name: string
  price: string | number
  image: string
  id: number | string
  type?: string
  categoryName?: string
  itemable?: {
    name: {
      ar: string
      en: string
    }
    description?: {
      ar: string
      en: string
    } | string
    originalData?: any
  }
}

const ProductCard: React.FC<ProductCardProps> = ({
  name,
  price,
  image,
  id,
  type,
  categoryName,
  itemable,
}) => {
  const { t, currentLanguage: language } = useTranslation()
  const { primaryColor, canOrder } = useTheme()
  const dispatch = useDispatch()
  const { showProductModal } = useSelector((state: RootState) => state.ui)
  const [selectedProductId, setSelectedProductId] = useState<string | number | null>(null)

  // Format number function for price display - Always use English numbers
  const formatNum = (num: string | number) => {
    return new Intl.NumberFormat('en-US').format(Number(num))
  }

  // Extract appropriate name for current language from itemable if available
  const getLocalizedName = () => {
    if (itemable && itemable.name) {
      return language === "ar" ? itemable.name.ar : itemable.name.en
    }
    return name || t("common.noName")
  }

  // Extract description from itemable if available with multilingual support
  const getDescription = () => {
    if (itemable && itemable.description) {
      // If description is an object with languages
      if (typeof itemable.description === 'object') {
        const desc = itemable.description as { ar?: string; en?: string }
        return language === "ar" ? (desc.ar || desc.en || '') : (desc.en || desc.ar || '')
      }
      // If description is a string
      return itemable.description as string
    }
    return ''
  }

  // Create a product object to pass to the modal
  const product = {
    id: id || `${name}-${price}`,
    name: getLocalizedName(),
    price: Number(price),
    image,
    description: getDescription(),
    type: type || "",
    categoryName: categoryName || "",
    itemable: itemable, // Add complete data for use in modal if needed
  }

  // Open the modal when the "+" button is clicked
  const handleOpenModal = () => {
    setSelectedProductId(id)
    dispatch(setShowProductModal(true))
  }

  // Close the modal
  const handleCloseModal = () => {
    setSelectedProductId(null)
    dispatch(setShowProductModal(false))
  }

  // Check for valid data to display
  if (!id || !price) {
    return null // Don't display card if data is incomplete
  }

  return (
    <>
      <div className="bg-white rounded-2xl duration-300 p-2.5 sm:p-3 lg:p-3.5 flex flex-col justify-between w-full h-full min-h-[200px] sm:min-h-[230px] md:min-h-[280px] lg:min-h-[320px]">
        {/* Product Image — full-width square frame so plates fill the card */}
        <div
          className="relative w-full aspect-square overflow-hidden rounded-xl bg-[#F7F7F5] mb-2 sm:mb-2.5 cursor-pointer group"
          onClick={handleOpenModal}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault()
              handleOpenModal()
            }
          }}
          aria-label={t("menu.addToCart")}
        >
          <img
            src={image && image.startsWith("http") ? image : `/${image || "b.png"}`}
            alt={getLocalizedName()}
            className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.onerror = null
              target.src = "/placeholder.png"
            }}
          />
        </div>

        {/* Product Info */}
        <div className="flex-shrink-0">
          <div className={`mb-2 sm:mb-3 ${language === "ar" ? "text-right" : "text-left"}`}>
            <h3 className="font-bold text-sm sm:text-base md:text-lg lg:text-xl text-gray-800 mb-1 sm:mb-2 line-clamp-2 leading-tight">
              {getLocalizedName()}
            </h3>
          </div>

          <div className={`flex justify-between items-center ${language === "ar" ? "text-right" : "text-left"}`}>
            {/* Price */}
            <div className="flex items-center gap-1.5 sm:gap-2" dir="ltr">
              <CurrencyDisplay className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold" iconStyle={{ color: primaryColor }}>
<svg xmlns="http://www.w3.org/2000/svg" width="16" height="19" viewBox="0 0 16 19" fill="none">
  <path d="M15.9999 15.0621C15.8942 15.9172 15.8479 16.2882 15.4531 17.1217L9.38959 18.3737C9.52897 17.4728 9.71455 16.7777 10.0165 16.361L15.9999 15.0621ZM7.55463 9.16565L9.36615 8.77307V3.04749C10.0413 2.28964 10.4565 1.94932 11.2714 1.51917V8.35901L15.9999 7.33264C15.8942 8.1875 15.8478 8.55802 15.4531 9.39124L11.2714 10.275V12.1989L15.9999 11.1979C15.8942 12.053 15.848 12.4239 15.4531 13.2574L11.2714 14.1207V14.1383L9.36615 14.5319V10.6774L7.55463 11.0602V13.4899L7.52338 13.4957C7.10666 14.2265 6.51821 15.1044 5.95111 15.8053L-6.10352e-05 16.9391C0.0533004 16.1734 0.164418 15.7416 0.510681 14.9703L5.64935 13.8561V11.4635L0.886658 12.4713C0.940015 11.7056 1.05218 11.2747 1.39838 10.5035L5.64935 9.57971V1.98206C6.32448 1.22421 6.73971 0.883893 7.55463 0.453735V9.16565Z" fill={primaryColor}/>
</svg>
              </CurrencyDisplay>
              <span className="text-base sm:text-lg md:text-xl lg:text-2xl font-bold" style={{ color: primaryColor }}>
                {price ? formatNum(price) : "-"}
              </span>
            </div>

            {/* Add Button — hidden when ordering is disabled via settings */}
            {canOrder && (
            <button
              className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 lg:w-14 lg:h-12 xl:w-14 xl:h-16 bg-white rounded-xl sm:rounded-xl  shadow-md shadow-gray-200   border border-gray-200 transition-all duration-300 "
              style={{
                color: primaryColor,

                filter: 'brightness(1)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.filter = 'brightness(0.9)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.filter = 'brightness(1)'
              }}
              onClick={handleOpenModal}
              aria-label={t("menu.addToCart")}
            >
              <span className="text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-light">+</span>
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal for selecting extras - Now positioned in center with full width and 80% height */}
      {showProductModal && selectedProductId === id && <ProductModal product={product} onClose={handleCloseModal} />}
    </>
  )
}

export default ProductCard
